import { BaseApiClient } from './base-client'
import { API_ROUTES, interpolateRoute } from '@super/shared-types'
import type {
  ApiResponse,
  PlatformCompanyUsage,
  PlatformInvocationDetail,
  PlatformInvocationRow,
  PlatformOffsetPage,
  PlatformWebhookDeliveryDetail,
  PlatformWebhookDeliveryRow,
  PlatformWebhookSubscriptionRow,
} from '@super/shared-types'

export type {
  PlatformCompanyUsage,
  PlatformInvocationDetail,
  PlatformInvocationRow,
  PlatformOffsetPage,
  PlatformWebhookDeliveryDetail,
  PlatformWebhookDeliveryRow,
  PlatformWebhookSubscriptionRow,
}

const ROUTES = API_ROUTES.ADMIN.PUBLIC_API

const EMPTY_PAGE = { items: [], total: 0, page: 1, pageSize: 15 }

export class AdminPublicApiClient extends BaseApiClient {
  async getCompanyUsage(
    companyId: string,
    window: '7' | '30',
  ): Promise<PlatformCompanyUsage | null> {
    const response = await this.request<ApiResponse<PlatformCompanyUsage>>(
      `${ROUTES.USAGE}${this.buildQueryString({ companyId, window })}`,
      { method: 'GET' },
    )
    return response.data ?? null
  }

  async listInvocations(params: {
    companyId?: string
    triggerId?: string
    status?: string
    search?: string
    since?: string
    page?: string
    limit?: string
  }): Promise<PlatformOffsetPage<PlatformInvocationRow>> {
    const response = await this.request<
      ApiResponse<PlatformOffsetPage<PlatformInvocationRow>>
    >(`${ROUTES.INVOCATIONS}${this.buildQueryString(params)}`, {
      method: 'GET',
    })
    return response.data ?? EMPTY_PAGE
  }

  async getInvocation(id: string): Promise<PlatformInvocationDetail> {
    const response = await this.request<ApiResponse<PlatformInvocationDetail>>(
      interpolateRoute(ROUTES.INVOCATION, { id }),
      { method: 'GET' },
    )
    return response.data!
  }

  async listWebhookSubscriptions(params: {
    companyId?: string
    page?: string
    limit?: string
  }): Promise<PlatformOffsetPage<PlatformWebhookSubscriptionRow>> {
    const response = await this.request<
      ApiResponse<PlatformOffsetPage<PlatformWebhookSubscriptionRow>>
    >(`${ROUTES.WEBHOOK_SUBSCRIPTIONS}${this.buildQueryString(params)}`, {
      method: 'GET',
    })
    return response.data ?? EMPTY_PAGE
  }

  async listWebhookDeliveries(params: {
    companyId?: string
    status?: string
    subscriptionId?: string
    since?: string
    page?: string
    limit?: string
  }): Promise<PlatformOffsetPage<PlatformWebhookDeliveryRow>> {
    const response = await this.request<
      ApiResponse<PlatformOffsetPage<PlatformWebhookDeliveryRow>>
    >(`${ROUTES.WEBHOOK_DELIVERIES}${this.buildQueryString(params)}`, {
      method: 'GET',
    })
    return response.data ?? EMPTY_PAGE
  }

  async getWebhookDelivery(id: string): Promise<PlatformWebhookDeliveryDetail> {
    const response = await this.request<
      ApiResponse<PlatformWebhookDeliveryDetail>
    >(interpolateRoute(ROUTES.WEBHOOK_DELIVERY, { id }), {
      method: 'GET',
    })
    return response.data!
  }
}

export const adminPublicApiClient = new AdminPublicApiClient()

const COMPANY_ROUTES = API_ROUTES.PUBLIC_API_OBSERVABILITY

/** Company-scoped observability — same reads, forced to the caller's company */
export class CompanyPublicApiClient extends BaseApiClient {
  async getUsage(window: '7' | '30'): Promise<PlatformCompanyUsage | null> {
    const response = await this.request<ApiResponse<PlatformCompanyUsage>>(
      `${COMPANY_ROUTES.USAGE}${this.buildQueryString({ window })}`,
      { method: 'GET' },
    )
    return response.data ?? null
  }

  async listInvocations(params: {
    triggerId?: string
    status?: string
    search?: string
    since?: string
    page?: string
    limit?: string
  }): Promise<PlatformOffsetPage<PlatformInvocationRow>> {
    const response = await this.request<
      ApiResponse<PlatformOffsetPage<PlatformInvocationRow>>
    >(`${COMPANY_ROUTES.INVOCATIONS}${this.buildQueryString(params)}`, {
      method: 'GET',
    })
    return response.data ?? EMPTY_PAGE
  }

  async getInvocation(id: string): Promise<PlatformInvocationDetail> {
    const response = await this.request<ApiResponse<PlatformInvocationDetail>>(
      interpolateRoute(COMPANY_ROUTES.INVOCATION, { id }),
      { method: 'GET' },
    )
    return response.data!
  }

  async listWebhookSubscriptions(params: {
    page?: string
    limit?: string
  }): Promise<PlatformOffsetPage<PlatformWebhookSubscriptionRow>> {
    const response = await this.request<
      ApiResponse<PlatformOffsetPage<PlatformWebhookSubscriptionRow>>
    >(
      `${COMPANY_ROUTES.WEBHOOK_SUBSCRIPTIONS}${this.buildQueryString(params)}`,
      { method: 'GET' },
    )
    return response.data ?? EMPTY_PAGE
  }

  async listWebhookDeliveries(params: {
    status?: string
    subscriptionId?: string
    since?: string
    page?: string
    limit?: string
  }): Promise<PlatformOffsetPage<PlatformWebhookDeliveryRow>> {
    const response = await this.request<
      ApiResponse<PlatformOffsetPage<PlatformWebhookDeliveryRow>>
    >(`${COMPANY_ROUTES.WEBHOOK_DELIVERIES}${this.buildQueryString(params)}`, {
      method: 'GET',
    })
    return response.data ?? EMPTY_PAGE
  }

  async getWebhookDelivery(id: string): Promise<PlatformWebhookDeliveryDetail> {
    const response = await this.request<
      ApiResponse<PlatformWebhookDeliveryDetail>
    >(interpolateRoute(COMPANY_ROUTES.WEBHOOK_DELIVERY, { id }), {
      method: 'GET',
    })
    return response.data!
  }
}

export const companyPublicApiClient = new CompanyPublicApiClient()
